import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Divider,
  Tooltip,
  Avatar,
  Paper
} from '@mui/material';
import LabelIcon from '@mui/icons-material/Label';
import PersonIcon from '@mui/icons-material/Person';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import BarChartIcon from '@mui/icons-material/BarChart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ErrorHandler from '@Root/ErrorHandler';
import LabelCardView from '@RegistriesViews/LabelCardView';
import LabelViewCreatedOverTime from '@LabelAnalyticsDashboardsLabelViewCharts/LabelViewCreatedOverTime';
import LabelViewUsageList from '@LabelAnalyticsDashboardsLabelViewCharts/LabelViewUsageList';
import NameCardService from '@Helpers/URLHelpers/WeboutService/NameCardService';
import moment from 'moment';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './LabelCardDisplay.scss';

export default function LabelCardDisplay(props) {
  const [, setImageUrl] = useState('');
  const [showLabelCardView, setShowLabelCardView] = useState(false);
  const [companyOwnerNameCard, setCompanyOwnerNameCard] = useState({});
  const [labelData, setLabelData] = useState();
  const [POCNameCard, setPOCNameCard] = useState({});
  const nameCardService = new NameCardService();
  const errorHandler = new ErrorHandler();
  const className = 'labelCardDisplay';

  useEffect(() => {
    renderData();
  }, [props]);

  const renderData = async () => {
    try {
      setLabelData({ ...props.labelCard, registryType: 'labelCard' });
      if (!!props.labelCard?.photo) {
        setImageUrl(URL.createObjectURL(dataURLtoFile(props.labelCard?.photo)));
      } else {
        setImageUrl('');
      }
      if (!!props.labelCard?.poc && props.labelCard?.poc !== 0) {
        let POCNameCardObj = await nameCardService.getById(props.labelCard?.poc);
        POCNameCardObj = JSON.parse(POCNameCardObj.body);
        setPOCNameCard(POCNameCardObj);
      }
      if (!!props.labelCard?.owner && props.labelCard?.owner !== 0) {
        let companyOwnerNameCardObj = await nameCardService.getById(props.labelCard?.owner);
        companyOwnerNameCardObj = JSON.parse(companyOwnerNameCardObj.body);
        setCompanyOwnerNameCard(companyOwnerNameCardObj);
      }
    } catch (err) {
      errorHandler.handleError(err);
    }
  };

  const dataURLtoFile = (dataurl) => {
    var arr = dataurl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], { type: mime });
  };

  const toggleLabelCardView = () => {
    setShowLabelCardView(!showLabelCardView);
  };

  const getLabelColor = (name) => {
    if (!name) return '#1976d2';
    const hash = name.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
    const colors = ['#1976d2', '#2e7d32', '#8e24aa', '#d32f2f', '#ef6c00', '#0288d1'];
    return colors[hash % colors.length];
  };

  const formatDateTime = (ts) => (ts ? moment(ts).format('MMM DD, YYYY [at] hh:mm A') : '—');

  return (
    <div className={className}>
      <Box className={`${className}__container`}>
        {/* Header Card */}
        <Card className={`${className}__headerCard`} elevation={0}>
          <CardContent className={`${className}__headerCard__content`}>
            <Box className={`${className}__topSection`}>
              {/* Primary Info */}
              <Box className={`${className}__primaryInfo`}>
                <Avatar
                  className={`${className}__avatar`}
                  style={{ background: getLabelColor(props?.labelCard?.name) }}
                >
                  <LabelIcon />
                </Avatar>
                <Box className={`${className}__nameSection`}>
                  <Typography variant="h5" className={`${className}__name`}>
                    {props?.labelCard?.name || 'Unnamed Label'}
                  </Typography>
                  {props.labelCard?.description && (
                    <Typography variant="body2" className={`${className}__description`}>
                      {props.labelCard.description}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box className={`${className}__actions`}>
                <Tooltip title="View Details">
                  <IconButton
                    className={`${className}__actionBtn ${className}__actionBtn--primary`}
                    onClick={toggleLabelCardView}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Analytics Overview">
                  <IconButton
                    className={`${className}__actionBtn ${className}__actionBtn--secondary`}
                    onClick={() => props?.handleLabelAnalytics(props.labelCard)}
                  >
                    <BarChartIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Divider className={`${className}__divider`} />

            {/* Details Section */}
            <Grid container spacing={2} className={`${className}__detailsSection`}>
              <Grid item xs={12} sm={6}>
                <Paper className={`${className}__detailCard`} elevation={0}>
                  <Box className={`${className}__detailCard__header`}>
                    <CalendarTodayIcon className={`${className}__detailCard__icon`} />
                    <Typography variant="caption" className={`${className}__detailCard__label`}>
                      Created
                    </Typography>
                  </Box>
                  <Typography variant="body2" className={`${className}__detailCard__value`}>
                    {formatDateTime(props?.labelCard?.createTimeStamp)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper className={`${className}__detailCard`} elevation={0}>
                  <Box className={`${className}__detailCard__header`}>
                    <CalendarTodayIcon className={`${className}__detailCard__icon`} />
                    <Typography variant="caption" className={`${className}__detailCard__label`}>
                      Updated
                    </Typography>
                  </Box>
                  <Typography variant="body2" className={`${className}__detailCard__value`}>
                    {formatDateTime(props?.labelCard?.modifiedTimeStamp)}
                  </Typography>
                </Paper>
              </Grid>
              {POCNameCard?.name && (
                <Grid item xs={12} sm={6}>
                  <Paper className={`${className}__detailCard`} elevation={0}>
                    <Box className={`${className}__detailCard__header`}>
                      <PersonIcon className={`${className}__detailCard__icon`} />
                      <Typography variant="caption" className={`${className}__detailCard__label`}>
                        Point of Contact
                      </Typography>
                    </Box>
                    <Typography variant="body2" className={`${className}__detailCard__value`}>
                      {POCNameCard.name}
                    </Typography>
                  </Paper>
                </Grid>
              )}
              {companyOwnerNameCard?.name && (
                <Grid item xs={12} sm={6}>
                  <Paper className={`${className}__detailCard`} elevation={0}>
                    <Box className={`${className}__detailCard__header`}>
                      <BusinessCenterIcon className={`${className}__detailCard__icon`} />
                      <Typography variant="caption" className={`${className}__detailCard__label`}>
                        Owner
                      </Typography>
                    </Box>
                    <Typography variant="body2" className={`${className}__detailCard__value`}>
                      {companyOwnerNameCard.name}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Analytics charts */}
        {!!labelData && (
          <Box className={`${className}__analyticsSection`}>
            <Card className={`${className}__analyticsCard`} elevation={0}>
              <CardContent>
                <LabelViewCreatedOverTime labelData={labelData} />
              </CardContent>
            </Card>
            <Card className={`${className}__analyticsCard`} elevation={0}>
              <CardContent>
                <LabelViewUsageList labelData={labelData} />
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>

      {showLabelCardView && (
        <LabelCardView
          open={showLabelCardView}
          closePopup={toggleLabelCardView}
          reloadData={props.reloadData}
          labelCard={props.labelCard}
        />
      )}
    </div>
  );
}
